import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../services/api'
import './Form.css';
import { notifyApplicationDraftSaved } from '../lib/applicationDrafts';
import { furiganaToFullWidth } from '../lib/kanaNormalize';
import { requiredFieldClass } from '../lib/requiredFieldClass';
import { extractLeadingPrefecture } from '../lib/postalAddress';

const DRAFT_KEY = 'draft_address_change';

const commonPrefixLength = (a, b) => {
    let i = 0;
    const u = String(a || '');
    const v = String(b || '');
    const lim = Math.min(u.length, v.length);
    while (i < lim && u[i] === v[i]) i += 1;
    return i;
};

const extractAddressTokens = (addrRaw) => {
    const addr = String(addrRaw || '').normalize('NFKC').replace(/\s+/g, '');
    // 例: 渋谷区恵比寿1-2-3 → ["渋谷区", "恵比寿"]
    const tokens = [];
    const mCity = addr.match(/.{2,15}?(?:市|区|町|村|郡)/g);
    if (mCity) tokens.push(...mCity);
    const trimmed = addr
        .replace(/[\d０-９]+/g, '')
        .replace(/[‐‑−ー―－\-]+/g, '')
        .replace(/(丁目|番地|番|号|階|F|Ｆ).*$/g, '');
    const pref = extractLeadingPrefecture(addr);
    const withoutPref = pref ? trimmed.slice(pref.length) : trimmed;
    const rest = withoutPref.replace(/.{2,15}?(?:市|区|町|村|郡)/g, '');
    if (rest && rest.length >= 2) tokens.push(rest);
    // ユニーク化
    return Array.from(new Set(tokens.filter(Boolean))).slice(0, 6);
};

/** ひらがな・カタカナ・長音のみを残しカタカナに統一（漢字・英数字はフリガナ欄に入れない） */
/** 注意: カタカナは U+30A1〜U+30FF まで（30F6 以前だけだと ワ・ヲ・ン 等が落ちる） */
const kanaOnlyToKatakana = (str) => {
    if (!str) return '';
    let out = '';
    for (const ch of str) {
        const c = ch.charCodeAt(0);
        if (c >= 0x3041 && c <= 0x3096) {
            out += String.fromCharCode(c + 0x60);
        } else if ((c >= 0x30a1 && c <= 0x30ff) || c === 0x31f0) {
            out += ch;
        } else if (c === 0x30fc || ch === 'ー') {
            out += 'ー';
        } else if (c === 0x30fb || ch === '・') {
            out += '・';
        }
    }
    return out;
};

/** 郵便番号APIの半角カナ等を NFKC で全角化してからカタカナ統一 */
const normalizePostalKana = (str) =>
    kanaOnlyToKatakana(String(str || '').normalize('NFKC'));

/**
 * postcode.teraren.com の返却キーは環境/版で揺れることがあるため、
 * 主要フィールドを安全に取り出す。
 */
const getPostcodeRowField = (row, ...keys) => {
    for (const k of keys) {
        const v = row?.[k];
        if (v !== undefined && v !== null && String(v).length) return v;
    }
    return '';
};

const normalizePrefectureName = (raw) => {
    const s = String(raw || '').normalize('NFKC').trim();
    if (!s) return '';
    // すでに都道府県の形ならそのまま
    if (/(都|道|府|県)$/.test(s)) return s;
    // よくある省略形の補完
    if (s === '北海道') return '北海道';
    if (s === '東京') return '東京都';
    if (s === '大阪') return '大阪府';
    if (s === '京都') return '京都府';
    // それ以外は県を付与（例: 青森 → 青森県）
    return `${s}県`;
};

const getPostcodePrefecture = (row) => getPostcodeRowField(row, 'prefecture', 'pref', 'address1', 'prefName');
const getPostcodeCity = (row) => getPostcodeRowField(row, 'city', 'address2', 'cityName');
const getPostcodeSuburb = (row) => getPostcodeRowField(row, 'suburb', 'town', 'address3', 'suburbName', 'townName');
const getPostcodeNewZip = (row) => getPostcodeRowField(row, 'new', 'zipcode', 'zip', 'postal_code');
const getPostcodePrefectureKana = (row) => getPostcodeRowField(row, 'prefecture_kana', 'pref_kana', 'kana1');
const getPostcodeCityKana = (row) => getPostcodeRowField(row, 'city_kana', 'cityKana', 'kana2');
const getPostcodeSuburbKana = (row) => getPostcodeRowField(row, 'suburb_kana', 'town_kana', 'suburbKana', 'kana3');

/**
 * 住所(漢字)の手入力部分を、フリガナ欄で使える形に寄せる。
 * - 「丁目」→「チョウメ」
 * - 数字・ハイフンは残す（例: 2丁目-1-1）
 * - それ以外の漢字は落とす（読みは手入力）
 */
const kanjiAddressExtraToKana = (str) => {
    const s = String(str || '').normalize('NFKC');
    const replaced = s.replace(/丁目/g, 'チョウメ');
    let out = '';
    for (const ch of replaced) {
        const c = ch.charCodeAt(0);
        if (c >= 0x30a1 && c <= 0x30ff) {
            out += ch;
        } else if (c >= 0x3041 && c <= 0x3096) {
            out += String.fromCharCode(c + 0x60);
        } else if (c >= 0x0030 && c <= 0x0039) {
            out += ch;
        } else if (ch === '-' || ch === '‐' || ch === '‑' || ch === '−' || ch === 'ー' || ch === '―' || ch === '－') {
            out += '-';
        } else if (ch === '・') {
            out += '・';
        }
    }
    return out;
};

/** teraren.com からカナを補完取得（zipcloudがカナ未提供の場合のフォールバック） */
const fetchKanaFromTeraren = async (digits) => {
    try {
        const res = await fetch(`https://postcode.teraren.com/postcodes/${digits}.json`);
        if (!res.ok) return '';
        const data = await res.json();
        const kana = [data.prefecture_kana, data.city_kana, data.suburb_kana].filter(Boolean).join('');
        return kana;
    } catch {
        return '';
    }
};

const AddressChangeForm = ({ onBack, onNext, isFirstForm, isLastForm, isMultiForm, id, isDraft }) => {
    const [formData, setFormData] = useState({
        oldZipCode: '',
        oldAddress: '',
        oldAddressKana: '',
        newZipCode:'',
        newAddress:'',
        newAddressKana:'',
        create_at:''
    });
    const [baseAddress, setBaseAddress] = useState({ addressKanji: '', addressKana: '' });
    /** 住所文字列から郵便番号逆引きした候補（postcode.teraren.com） */
    const [addressZipCandidates, setAddressZipCandidates] = useState([]);
    const [zipSearching, setZipSearching] = useState(false);
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    // 郵便番号から住所を自動入力するための参照
    const newAddressRef  = useRef(null);

    const isComposing = useRef(false);

    const formatZipcode = (value) => {
        let v = value.replace(/\D/g, "");
        if (v.length > 7) v = v.slice(0, 7);
        if (v.length >= 4) return v.slice(0, 3) + "-" + v.slice(3);
        return v;
    };

    const fetchAddressFromZipcode = async (zipcode) => {
        const digits = zipcode.replace(/\D/g, "");
        if (digits.length !== 7) return null;
        try {
            const res = await fetch(`https://zipcloud.ibsnet.co.jp/api/search?zipcode=${digits}`);
            const json = await res.json();
            if (json.status === 200 && json.results && json.results[0]) {
                const r = json.results[0];
                const addressKanji = [r.address1, r.address2, r.address3].filter(Boolean).join("");
                let addressKana = [r.kana1, r.kana2, r.kana3].filter(Boolean).join("");
                // zipcloudがカナ未提供の場合はteraren.comで補完
                if (!addressKana) {
                    addressKana = await fetchKanaFromTeraren(digits);
                }
                return { addressKanji, addressKana };
            }
        } catch (err) {
            console.warn("郵便番号検索エラー:", err);
        }
        return null;
    };

    const applyPostcodeCandidate = (r) => {
        const digits = String(getPostcodeNewZip(r) || '').replace(/\D/g, '');
        const z = formatZipcode(digits);
        const addressKanji = [getPostcodePrefecture(r), getPostcodeCity(r), getPostcodeSuburb(r)].filter(Boolean).join('');
        const addressKana = [getPostcodePrefectureKana(r), getPostcodeCityKana(r), getPostcodeSuburbKana(r)].filter(Boolean).join('');
        setBaseAddress({ addressKanji, addressKana });
        setFormData((prev) => {
            const addr = prev.newAddress || '';
            const nextKana =
                addressKanji && addr.startsWith(addressKanji)
                    ? normalizePostalKana(addressKana) +
                      kanjiAddressExtraToKana(addr.substring(addressKanji.length))
                    : normalizePostalKana(addressKana) + kanjiAddressExtraToKana(addr);
            return {
                ...prev,
                newZipCode: z,
                newAddressKana: nextKana,
            };
        });
        setAddressZipCandidates([]);
    };

    /** 郵便番号（7桁数字）から住所を取得してフォームに適用する */
    const applyZipCode = async (digits) => {
        const z = formatZipcode(digits);
        const addr = await fetchAddressFromZipcode(z);
        if (addr) {
            setBaseAddress(addr);
            setFormData((prev) => {
                const currentAddr = prev.newAddress || '';
                const nextKana =
                    addr.addressKanji && currentAddr.startsWith(addr.addressKanji)
                        ? normalizePostalKana(addr.addressKana) +
                          kanjiAddressExtraToKana(currentAddr.substring(addr.addressKanji.length))
                        : normalizePostalKana(addr.addressKana) +
                          kanjiAddressExtraToKana(currentAddr);
                return { ...prev, newZipCode: z, newAddressKana: nextKana };
            });
        } else {
            setFormData((prev) => ({ ...prev, newZipCode: z }));
        }
        setAddressZipCandidates([]);
    };

    const lookupZipFromAddress = async (addrRaw) => {
        const addr = addrRaw.normalize('NFKC').trim();
        if (addr.length < 5) return;

        const pref = extractLeadingPrefecture(addr);

        try {
            const url = `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(addr)}&format=json&addressdetails=1&countrycodes=jp&limit=10&accept-language=ja`;
            const res = await fetch(url, {
                headers: { 'User-Agent': 'stleducation-employee-app/1.0' },
            });
            const results = await res.json();

            if (!Array.isArray(results) || results.length === 0) {
                setAddressZipCandidates([]);
                return;
            }

            const seen = new Set();
            const candidates = [];

            for (const item of results) {
                const postcode = item.address?.postcode;
                if (!postcode) continue;
                const digits = postcode.replace(/\D/g, '');
                if (digits.length !== 7) continue;

                // 都道府県フィルタ
                if (pref) {
                    const itemPref = normalizePrefectureName(item.address?.province || '');
                    if (itemPref && itemPref !== pref) continue;
                }

                if (seen.has(digits)) continue;
                seen.add(digits);

                const displayPref = item.address?.province || '';
                const displayCity = item.address?.city || item.address?.town || item.address?.county || '';
                const displaySub = item.address?.neighbourhood || item.address?.suburb || item.address?.quarter || '';
                candidates.push({ zip: digits, label: `${displayPref}${displayCity}${displaySub}` });
            }

            if (candidates.length === 0) {
                setAddressZipCandidates([]);
                return;
            }

            if (candidates.length === 1) {
                await applyZipCode(candidates[0].zip);
            } else {
                setAddressZipCandidates(candidates);
            }
        } catch (err) {
            console.warn('住所検索エラー:', err);
            setAddressZipCandidates([]);
        }
    };

    const handleNewAddressBlur = (e) => {
        lookupZipFromAddress(e.target.value);
    };

    const handleSearchZipButton = async () => {
        const addr = formData.newAddress;
        if (!addr || addr.trim().length < 4) {
            alert('住所（漢字）を入力してから検索してください。');
            return;
        }
        setZipSearching(true);
        await lookupZipFromAddress(addr);
        setZipSearching(false);
    };

    const handleInputChange = async (e) => {
        const { name, value } = e.target;
        if (name === "newZipCode") {
            const formatted = formatZipcode(value);
            setFormData((prev) => ({
                ...prev,
                newZipCode: formatted,
            }));
            if (formatted.replace(/\D/g, "").length === 7) {
                const addr = await fetchAddressFromZipcode(formatted);
                if (addr) {
                    setBaseAddress(addr);
                    setFormData((prev) => ({
                        ...prev,
                        newZipCode: formatted,
                        newAddress: addr.addressKanji,
                        newAddressKana: normalizePostalKana(addr.addressKana),
                    }));
                    // 郵便番号入力された後、新住所のフィールドにフォーカスを当てる
                    //setTimeout(() => newAddressRef.current?.focus(), 100);
                    setTimeout(() => {
                        if (newAddressRef.current && typeof newAddressRef.current.focus === 'function') {
                            newAddressRef.current.focus();
                        }
                    }, 100);
                }
            }
            return;
        }
        // 住所入力の漢字とフリガナの同じ部分を自動更新
        if (name === "newAddress") {
            // IMEでの入力中は住所のみ更新（フリガナは確定後に同期）
            if (isComposing.current) {
                setFormData(prev => ({ ...prev, newAddress: value }));
                return;
            }
            // カナベースが取得できていない場合は住所のみ更新（フリガナは手動入力）
            if (!baseAddress.addressKana) {
                setFormData(prev => ({ ...prev, newAddress: value }));
                return;
            }
            let nextKana;
            if (baseAddress.addressKanji && value.startsWith(baseAddress.addressKanji)) {
                const extra = value.substring(baseAddress.addressKanji.length);
                nextKana = normalizePostalKana(baseAddress.addressKana) + kanjiAddressExtraToKana(extra);
            } else {
                nextKana = normalizePostalKana(baseAddress.addressKana) + kanjiAddressExtraToKana(value.substring(
                    Math.max(0, commonPrefixLength(value, baseAddress.addressKanji))
                ));
            }
            setFormData(prev => ({ ...prev, newAddress: value, newAddressKana: nextKana }));
            return;
        }
        if (name === 'newAddressKana') {
            setFormData((prev) => ({
                ...prev,
                newAddressKana: normalizePostalKana(furiganaToFullWidth(value)),
            }));
            return;
        }

        setFormData(prevData => ({
            ...prevData,
            [name]: value
        }));
    };
    
    // 入力中の文字が確定していない可能性があるため、IMEの確定イベントを処理
    const handleCompositionEnd = (e) => {
        isComposing.current = false;
        setTimeout(() => handleInputChange(e), 50);
    };

    useEffect(() => {
    (async () => {
        try {
            const res = await api.get("/api/me/");
            const me = res.data;

            setFormData((prev) => ({
                ...prev,
                oldZipCode: me.zipCode || "",
                oldAddress: me.address || "",
                oldAddressKana: me.addressKana || ""
            }));
        } catch (e) {
            alert("認証エラー。再ログインしてください。");
            navigate("/m/login");
            }
        })();
    }, [navigate]);

    useEffect(() => {
        try {
            const saved = localStorage.getItem(DRAFT_KEY);
            if (saved) {
                const parsed = JSON.parse(saved);
                setFormData(prev => ({ ...prev, newZipCode: parsed.newZipCode || '', newAddress: parsed.newAddress || '', newAddressKana: parsed.newAddressKana || '', create_at: parsed.create_at || '' }));
            }
        } catch (_) {}
    }, []);
    
    const handleSaveDraft = () => {
        localStorage.setItem(DRAFT_KEY, JSON.stringify({ newZipCode: formData.newZipCode, newAddress: formData.newAddress, newAddressKana: formData.newAddressKana, create_at: formData.create_at }));
        notifyApplicationDraftSaved();
        alert('一時保存しました。申請一覧では「一時保存」と表示されます。');
    };

    const handleDeleteDraft = async () => {
        const confirmDelete = window.confirm("この下書き（一時保存データ）を削除してもよろしいですか？\n※削除すると元に戻せません。");
        if (!confirmDelete) return;

        setLoading(true);
        try {
            if (id) {
                await api.delete(`/api/applications/drafts/${id}/`);
            }    
            // ローカルでも削除
            localStorage.removeItem(DRAFT_KEY);
            alert('下書きを削除しました。');
            navigate('/applications');
        } catch (error) {
            console.error('下書きの削除に失敗:', error);
            alert('下書きの削除に失敗しました。もう一度お試しください。');
        } finally {
            setLoading(false);
        }
    };

    const handleBack = () => { if (onBack) onBack(); else navigate('/applications'); };
    
    //　フォームの送信を処理
    const handleSubmit = async (e) => {
        e.preventDefault();

        const isConfirmed = window.confirm("住所変更申請を送信してもよろしいですか？");
    
        if (!isConfirmed) {
            return; 
        }

        const today = new Date();
        const dateStr = `${today.getFullYear()}-${String(today.getMonth()+1).padStart(2,'0')}-${String(today.getDate()).padStart(2,'0')}`;
        const payload = {
            oldZipCode: formData.oldZipCode,
            oldAddress: formData.oldAddress,
            oldAddressKana: formData.oldAddressKana,
            newZipCode: formData.newZipCode,
            newAddress: formData.newAddress,
            newAddressKana: formData.newAddressKana,
            created_at: formData.create_at || dateStr,
        }

        try {
            await api.post('/api/forms/submit_address_change/', payload);
            localStorage.removeItem(DRAFT_KEY);
            const goTrains = window.confirm('住所変更が正常に提出されました。\n\n交通機関を申請しますか？\n\n「OK」→ 交通機関申請画面へ\n「キャンセル」→ 申請一覧に戻る');
            if (goTrains) {
                navigate('/selected-forms?ids=trains_change');
            } else {
                navigate('/applications');
            }
        } catch (error) {
            console.error('Submission failed:', error);
            alert('申請に失敗しました。もう一度お試しください。');
            
        }
    };

    return(
        <div className="container form-container">
            <h3>住所変更</h3>	
            
            <form onSubmit={handleSubmit}>
                <div className="mb-3">
                    <label htmlFor="oldZipCode" className="form-label">旧郵便番号</label>
                    <input type="text" id="oldZipCode" name="oldZipCode" className="form-control" value={formData.oldZipCode} readOnly />
                </div>
                <div className="mb-3">
                    <label htmlFor="oldAddress" className="form-label">旧住所（漢字）</label>
                    <input type="text" id="oldAddress" name="oldAddress" className="form-control" value={formData.oldAddress} readOnly />
                </div>
                <div className="mb-4">
                    <label htmlFor="oldAddressKana" className="form-label">旧住所（フリガナ）</label>
                    <small className="form-hint">マンションの場合、マンション名も記入</small>
                    <input type="text" id="oldAddressKana" name="oldAddressKana" className="form-control" value={formData.oldAddressKana} readOnly />
                </div>

                <div className="mb-3">
                    <label htmlFor="newZipCode" className="form-label">新郵便番号</label>
                    <input type="text" id="newZipCode" name="newZipCode" className={requiredFieldClass(formData.newZipCode)} value={formData.newZipCode} onChange={handleInputChange} required />
                </div>
                <div className="mb-3">
                    <label htmlFor="newAddress" className="form-label">新住所（漢字）</label>
                    <small className="form-hint">マンションの場合、マンション名も記入</small>
                    <input ref={newAddressRef} type="text" id="newAddress" name="newAddress" className={requiredFieldClass(formData.newAddress)} value={formData.newAddress} onChange={handleInputChange} onBlur={handleNewAddressBlur} onCompositionStart={() => { isComposing.current = true; }}
                        onCompositionEnd={handleCompositionEnd} required />
                    <button
                        type="button"
                        onClick={handleSearchZipButton}
                        disabled={zipSearching}
                        style={{
                            marginTop: 8, padding: '8px 14px',
                            background: zipSearching ? '#9ca3af' : '#2563eb',
                            color: '#fff', border: 'none', borderRadius: 6,
                            fontWeight: 600, fontSize: '0.9rem', cursor: zipSearching ? 'default' : 'pointer',
                            width: '100%',
                        }}
                    >
                        {zipSearching ? '🔍 検索中...' : '🔍 郵便番号・フリガナを自動入力'}
                    </button>
                    <p className="form-suggest-hint">住所（漢字）を入力後、上のボタンをタップすると郵便番号・フリガナを自動検索します。候補が複数のときは一覧から選んでください。</p>
                    {addressZipCandidates.length > 1 && (
                        <div className="form-suggest-box" role="listbox" aria-label="郵便番号候補">
                            {addressZipCandidates.map((c) => {
                                const formattedZip = c.zip.replace(/(\d{3})(\d{4})/, '$1-$2');
                                const label = `〒${formattedZip} ${c.label}`;
                                return (
                                    <button
                                        key={c.zip}
                                        type="button"
                                        className="form-suggest-item"
                                        onClick={() => applyZipCode(c.zip)}
                                    >
                                        {label}
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </div>
                <div className="mb-3">
                    <label htmlFor="newAddressKana" className="form-label">新住所（フリガナ）</label>
                    <small className="form-hint">マンションの場合、マンション名も記入</small>
                    <input type="text" id="newAddressKana" name="newAddressKana" className="form-control" value={formData.newAddressKana} onChange={handleInputChange} />
                </div>
                <div className="mb-3">
                    <label htmlFor="create_at" className="form-label">申請日</label>
                    <input type="date" id="create_at" name="create_at" className="form-control" value={requiredFieldClass(formData.create_at)} onChange={handleInputChange} />
                </div>

            
            
            <div className="button-group">
                <button type="submit" className="btn-submit">申請</button>
                <button type="button" className="btn-draft" onClick={handleSaveDraft}>一時保存</button>
                {((id && isDraft) || localStorage.getItem(DRAFT_KEY)) && <button type="button" className="btn-delete-draft" onClick={handleDeleteDraft} disabled={loading}>下書削除</button>}
                <button type="button" className="btn-back" onClick={handleBack}>{onBack && !isFirstForm ? '前へ' : '戻る'}</button>
                {isMultiForm && onNext && <button type="button" className="btn-nav" onClick={onNext}>{isLastForm ? '完了' : '次へ'}</button>}
            </div>
            </form>
        </div>
    )


};
export default AddressChangeForm;